import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Loader2 } from "lucide-react";
import { CURRENCIES } from "../../lib/currency";

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + "/api";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// Currency Vyomos bills this academy in. Plans stay USD-priced; each charge
// converts at the live daily exchange rate.
export default function BillingCurrencyCard({ tenantId, current, planAmountUsd }) {
  const [value, setValue] = useState(current || "usd");
  const [saved, setSaved] = useState(current || "usd");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(current || "usd"); setSaved(current || "usd"); }, [current]);

  useEffect(() => {
    if (value === "usd" || !planAmountUsd) { setPreview(null); return; }
    axios.get(`${API}/super-admin/fx-preview`, { params: { amount_usd: planAmountUsd, currency: value }, headers: auth() })
      .then((r) => setPreview(r.data)).catch(() => setPreview({ error: true }));
  }, [value, planAmountUsd]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super-admin/tenants/${tenantId}/billing-currency`, { currency: value }, { headers: auth() });
      setSaved(value);
      toast.success("Billing currency saved — applies from the next invoice");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save billing currency");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3" data-testid="billing-currency-card">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Coins className="h-5 w-5 text-amber-500" />Billing currency</h3>
      <p className="text-xs text-slate-500">
        Plans are priced in US Dollars. When billing in another currency, each charge converts the USD amount at that day's live exchange rate. Only future invoices change.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <select value={value} onChange={(e) => setValue(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="billing-currency-select">
          {Object.entries(CURRENCIES).map(([code, c]) => (
            <option key={code} value={code}>{c.symbol} · {code.toUpperCase()} — {c.name}</option>
          ))}
        </select>
        <button type="button" onClick={save} disabled={saving || value === saved}
                className="inline-flex h-9 items-center rounded-md bg-slate-900 px-4 text-sm text-white disabled:opacity-50" data-testid="billing-currency-save">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
        </button>
      </div>
      {preview && !preview.error && (
        <p className="text-sm text-slate-700" data-testid="billing-currency-preview">
          ${Number(preview.amount_usd).toFixed(2)} USD ≈ <strong>{CURRENCIES[value].symbol}{Number(preview.amount).toFixed(2)}</strong> today (rate {preview.fx_rate}, {preview.fx_as_of})
        </p>
      )}
      {preview?.error && <p className="text-sm text-red-600" data-testid="billing-currency-preview-error">Live exchange rate unavailable right now.</p>}
    </div>
  );
}
